import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {Button, Form, Modal, Spinner, InputGroup} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {Formik, Form as FormikForm} from 'formik'
import * as Yup from 'yup'
import {StarRatingDisplay} from './components/StarRatingDisplay'
import {
  fetchAdminReviews,
  moderateReview,
  replyToReview,
  toggleFeatured,
  deleteReview,
  fetchToursForSelect,
  TourReviewAdmin,
  ReviewSource,
  ReviewStatus,
} from './reviewsManagementApi'

const PENDING_QK = ['tour-reviews-pending-count']
const ADMIN_QK = 'tour-reviews-admin'

function truncate(s: string, n: number) {
  if (!s) return ''
  return s.length <= n ? s : `${s.slice(0, n)}…`
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
  } catch {
    return iso
  }
}

const StatusBadge = ({status}: {status: ReviewStatus}) => {
  const styles: Record<ReviewStatus, {bg: string; color: string}> = {
    approved: {bg: '#a1efff', color: '#006573'},
    pending: {bg: '#ffdcbe', color: '#693c00'},
    rejected: {bg: '#ffdad6', color: '#93000a'},
  }
  const s = styles[status] || {bg: '#eceef0', color: '#434652'}
  return (
    <span
      style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        backgroundColor: s.bg,
        color: s.color,
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  )
}

const SourceBadge = ({source}: {source: ReviewSource}) => {
  const label =
    source === 'internal' ? 'Direct' : source === 'google' ? 'Google' : 'TripAdvisor'
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        backgroundColor: '#f2f4f6',
        color: '#434652',
        fontSize: '11px',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

const ReviewsModerationTab: React.FC = () => {
  const intl = useIntl()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | 'all'>('pending')
  const [sourceFilter, setSourceFilter] = useState<ReviewSource | 'all'>('all')
  const [tourFilter, setTourFilter] = useState('')
  const [ratingFilter, setRatingFilter] = useState<number | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 15

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [detail, setDetail] = useState<TourReviewAdmin | null>(null)
  const [rejectTarget, setRejectTarget] = useState<string[] | null>(null)
  const [replyTarget, setReplyTarget] = useState<TourReviewAdmin | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TourReviewAdmin | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const {data: tours = []} = useQuery(['tours-for-reviews-filter'], fetchToursForSelect, {
    staleTime: 60_000,
  })

  const listParams = useMemo(() => {
    const status: '' | ReviewStatus = statusFilter === 'all' ? '' : statusFilter
    const source: '' | ReviewSource = sourceFilter === 'all' ? '' : sourceFilter
    return {
      page,
      limit: pageSize,
      status,
      source,
      tour: tourFilter || undefined,
      rating: ratingFilter === '' ? undefined : ratingFilter,
      search: debouncedSearch || undefined,
    }
  }, [page, pageSize, statusFilter, sourceFilter, tourFilter, ratingFilter, debouncedSearch])

  const {data, isLoading, refetch} = useQuery(
    [ADMIN_QK, listParams],
    () => fetchAdminReviews(listParams),
    {keepPreviousData: true}
  )

  const rows = data?.data ?? []
  const pagination = data?.payload?.pagination

  const invalidateAll = () => {
    void qc.invalidateQueries([ADMIN_QK])
    void qc.invalidateQueries(PENDING_QK)
  }

  const modMut = useMutation(
    async ({ids, status, note}: {ids: string[]; status: 'approved' | 'rejected'; note?: string}) => {
      for (const id of ids) {
        await moderateReview(id, {status, moderationNote: note})
      }
    },
    {
      onSuccess: () => {
        void toast.success(intl.formatMessage({id: 'REVIEWS.MODERATION.SUCCESS'}))
        setSelected({})
        setRejectTarget(null)
        invalidateAll()
        void refetch()
      },
      onError: () => void toast.error(intl.formatMessage({id: 'REVIEWS.MODERATION.ERROR'})),
    }
  )

  const replyMut = useMutation(
    ({id, text}: {id: string; text: string}) => replyToReview(id, text),
    {
      onSuccess: () => {
        void toast.success(intl.formatMessage({id: 'REVIEWS.REPLY.SUCCESS'}))
        setReplyTarget(null)
        invalidateAll()
        void refetch()
      },
      onError: () => void toast.error(intl.formatMessage({id: 'REVIEWS.REPLY.ERROR'})),
    }
  )

  const featMut = useMutation((id: string) => toggleFeatured(id), {
    onSuccess: () => {
      void toast.success(intl.formatMessage({id: 'REVIEWS.FEATURED.TOGGLE_OK'}))
      invalidateAll()
      void refetch()
    },
    onError: () => void toast.error(intl.formatMessage({id: 'REVIEWS.FEATURED.ERROR'})),
  })

  const delMut = useMutation((id: string) => deleteReview(id), {
    onSuccess: () => {
      void toast.success(intl.formatMessage({id: 'REVIEWS.DELETE.SUCCESS'}))
      setDeleteTarget(null)
      invalidateAll()
      void refetch()
    },
    onError: () => void toast.error(intl.formatMessage({id: 'REVIEWS.DELETE.ERROR'})),
  })

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  )

  const toggleRow = (id: string) => setSelected((s) => ({...s, [id]: !s[id]}))

  const toggleAllPage = () => {
    const allOn = rows.length > 0 && rows.every((r) => selected[r._id])
    const next: Record<string, boolean> = {...selected}
    rows.forEach((r) => {next[r._id] = !allOn})
    setSelected(next)
  }

  const tourTitle = (r: TourReviewAdmin) => {
    const t = r.tour as {title?: string} | null | undefined
    return t?.title ?? intl.formatMessage({id: 'REVIEWS.TABLE.NO_TOUR'})
  }

  const selectStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#434652',
    cursor: 'pointer',
    padding: '0.5rem 0.25rem',
  }

  return (
    <>
      {/* ── Azure Curator Filter Bar ─────────────────────────────────────── */}
      <div
        className='reviews-filter-grid'
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <div style={{backgroundColor: '#f2f4f6', padding: '0.375rem 0.75rem', borderRadius: '1rem'}}>
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => {setPage(0); setStatusFilter(e.target.value as ReviewStatus | 'all')}}
          >
            <option value='all'>Status: All</option>
            <option value='pending'>Pending</option>
            <option value='approved'>Approved</option>
            <option value='rejected'>Rejected</option>
          </select>
        </div>
        <div style={{backgroundColor: '#f2f4f6', padding: '0.375rem 0.75rem', borderRadius: '1rem'}}>
          <select
            style={selectStyle}
            value={sourceFilter}
            onChange={(e) => {setPage(0); setSourceFilter(e.target.value as ReviewSource | 'all')}}
          >
            <option value='all'>Source: All</option>
            <option value='internal'>Direct</option>
            <option value='google'>Google</option>
            <option value='tripadvisor'>TripAdvisor</option>
          </select>
        </div>
        <div style={{backgroundColor: '#f2f4f6', padding: '0.375rem 0.75rem', borderRadius: '1rem'}}>
          <select
            style={selectStyle}
            value={tourFilter}
            onChange={(e) => {setPage(0); setTourFilter(e.target.value)}}
          >
            <option value=''>Tour: All Tours</option>
            {tours.map((t) => (
              <option key={t._id} value={t._id}>{t.label}</option>
            ))}
          </select>
        </div>
        <div style={{backgroundColor: '#f2f4f6', padding: '0.375rem 0.75rem', borderRadius: '1rem'}}>
          <select
            style={selectStyle}
            value={ratingFilter === '' ? '' : String(ratingFilter)}
            onChange={(e) => {setPage(0); const v = e.target.value; setRatingFilter(v === '' ? '' : Number(v))}}
          >
            <option value=''>Rating: Any</option>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
          </select>
        </div>
        <div
          style={{
            backgroundColor: '#ffdcbe',
            padding: '0.375rem 0.75rem',
            borderRadius: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setPage(0)}
        >
          <span style={{color: '#2c1600', fontWeight: 700, fontSize: '0.875rem'}}>Apply Filters</span>
        </div>
      </div>

      {/* Search bar */}
      <div style={{marginBottom: '1.5rem'}}>
        <div style={{position: 'relative', maxWidth: '28rem'}}>
          <span
            className='material-symbols-outlined'
            style={{position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#737783'}}
          >
            search
          </span>
          <input
            type='text'
            value={searchInput}
            onChange={(e) => {setPage(0); setSearchInput(e.target.value)}}
            placeholder={intl.formatMessage({id: 'REVIEWS.FILTER.SEARCH_PLACEHOLDER'})}
            style={{
              width: '100%',
              paddingLeft: '2.5rem',
              paddingRight: '1rem',
              paddingTop: '0.625rem',
              paddingBottom: '0.625rem',
              borderRadius: '9999px',
              border: 'none',
              backgroundColor: '#f2f4f6',
              fontSize: '0.875rem',
              outline: 'none',
              color: '#191c1e',
            }}
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center',
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: '#f2f4f6',
            borderRadius: '0.75rem',
          }}
        >
          <span style={{fontWeight: 600, fontSize: '0.875rem', color: '#191c1e', marginRight: '0.5rem'}}>
            {intl.formatMessage({id: 'REVIEWS.BULK.SELECTED'}, {n: selectedIds.length})}
          </span>
          <Button size='sm' variant='success' disabled={modMut.isLoading}
            onClick={() => modMut.mutate({ids: selectedIds, status: 'approved'})}>
            {intl.formatMessage({id: 'REVIEWS.ACTION.APPROVE'})}
          </Button>
          <Button size='sm' variant='danger' disabled={modMut.isLoading}
            onClick={() => setRejectTarget(selectedIds)}>
            {intl.formatMessage({id: 'REVIEWS.ACTION.REJECT'})}
          </Button>
        </div>
      )}

      {/* ── Azure Curator Data Table ──────────────────────────────────────── */}
      <div style={{backgroundColor: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 24px -4px rgba(0,52,127,0.06)', overflow: 'hidden', marginBottom: '2rem'}}>
        {isLoading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem'}}>
            <Spinner animation='border' style={{color: '#00347f'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%'}}>
            <table style={{width: 'max-content', minWidth: 900, borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#e6e8ea'}}>
                  <th style={{padding: '1rem 1.5rem', width: '40px'}}>
                    <input
                      type='checkbox'
                      checked={rows.length > 0 && rows.every((r) => selected[r._id])}
                      onChange={toggleAllPage}
                      style={{cursor: 'pointer'}}
                    />
                  </th>
                  {['Date', 'Tour / Activity', 'Author', 'Source', 'Rating', 'Comment', 'Status', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          padding: '1rem 1.5rem',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: 'rgba(67, 70, 82, 0.8)',
                          whiteSpace: 'nowrap',
                          textAlign: h === 'Actions' ? 'right' : 'left',
                          border: 'none',
                        }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r._id}
                    style={{
                      borderTop: '1px solid #eceef0',
                      backgroundColor: 'white',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(242,244,246,0.4)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <td style={{padding: '1.25rem 1.5rem'}}>
                      <input
                        type='checkbox'
                        checked={!!selected[r._id]}
                        onChange={() => toggleRow(r._id)}
                        style={{cursor: 'pointer'}}
                      />
                    </td>
                    <td style={{padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: '#434652', whiteSpace: 'nowrap'}}>
                      {formatDate(r.reviewDate || r.createdAt)}
                    </td>
                    <td style={{padding: '1.25rem 1.5rem'}}>
                      <span style={{fontSize: '0.875rem', fontWeight: 700, color: '#00347f', display: 'block'}}>
                        {truncate(tourTitle(r), 30)}
                      </span>
                    </td>
                    <td style={{padding: '1.25rem 1.5rem', fontSize: '0.875rem', fontWeight: 500}}>
                      {r.authorName}
                    </td>
                    <td style={{padding: '1.25rem 1.5rem'}}>
                      <SourceBadge source={r.source} />
                    </td>
                    <td style={{padding: '1.25rem 1.5rem'}}>
                      <StarRatingDisplay value={r.rating} />
                    </td>
                    <td style={{padding: '1.25rem 1.5rem', maxWidth: '16rem'}}>
                      <p style={{
                        fontSize: '0.875rem',
                        color: '#434652',
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                        margin: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        "{truncate(r.comment, 100)}"
                      </p>
                    </td>
                    <td style={{padding: '1.25rem 1.5rem'}}>
                      <StatusBadge status={r.status} />
                    </td>
                    <td style={{padding: '1.25rem 1.5rem', textAlign: 'right'}}>
                      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.25rem', flexWrap: 'wrap'}}>
                        {r.status === 'pending' && (
                          <>
                            <button
                              style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', fontSize: '0', display: 'flex', alignItems: 'center'}}
                              disabled={modMut.isLoading}
                              onClick={() => modMut.mutate({ids: [r._id], status: 'approved'})}
                              title={intl.formatMessage({id: 'REVIEWS.ACTION.APPROVE'})}
                              onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#006876'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0,104,118,0.1)'}}
                              onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                            >
                              <span className='material-symbols-outlined' style={{fontSize: '18px'}}>check_circle</span>
                            </button>
                            <button
                              style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                              disabled={modMut.isLoading}
                              onClick={() => setRejectTarget([r._id])}
                              title={intl.formatMessage({id: 'REVIEWS.ACTION.REJECT'})}
                              onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#ba1a1a'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(186,26,26,0.1)'}}
                              onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                            >
                              <span className='material-symbols-outlined' style={{fontSize: '18px'}}>cancel</span>
                            </button>
                          </>
                        )}
                        {r.status === 'approved' && (
                          <button
                            style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                            onClick={() => setReplyTarget(r)}
                            title={intl.formatMessage({id: 'REVIEWS.ACTION.REPLY'})}
                            onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#00347f'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0,52,127,0.08)'}}
                            onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                          >
                            <span className='material-symbols-outlined' style={{fontSize: '18px'}}>reply</span>
                          </button>
                        )}
                        {r.status === 'rejected' && (
                          <button
                            style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                            onClick={() => modMut.mutate({ids: [r._id], status: 'approved'})}
                            title='Restore'
                            onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#00347f'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0,52,127,0.08)'}}
                            onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                          >
                            <span className='material-symbols-outlined' style={{fontSize: '18px'}}>undo</span>
                          </button>
                        )}
                        <button
                          style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                          disabled={featMut.isLoading}
                          onClick={() => featMut.mutate(r._id)}
                          title={intl.formatMessage({id: 'REVIEWS.ACTION.FEATURED'})}
                          onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#00347f'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0,52,127,0.08)'}}
                          onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                        >
                          <span className='material-symbols-outlined' style={{fontSize: '18px', fontVariationSettings: r.isFeatured ? "'FILL' 1" : "'FILL' 0"}}>star</span>
                        </button>
                        <button
                          style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                          onClick={() => setDetail(r)}
                          title={intl.formatMessage({id: 'REVIEWS.ACTION.DETAIL'})}
                          onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#00347f'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0,52,127,0.08)'}}
                          onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                        >
                          <span className='material-symbols-outlined' style={{fontSize: '18px'}}>visibility</span>
                        </button>
                        <button
                          style={{padding: '0.375rem 0.625rem', borderRadius: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#434652', display: 'flex', alignItems: 'center'}}
                          onClick={() => setDeleteTarget(r)}
                          title={intl.formatMessage({id: 'REVIEWS.ACTION.DELETE'})}
                          onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#ba1a1a'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(186,26,26,0.1)'}}
                          onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.color = '#434652'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'}}
                        >
                          <span className='material-symbols-outlined' style={{fontSize: '18px'}}>delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{padding: '6rem 2rem', textAlign: 'center', color: '#737783'}}>
                      <span className='material-symbols-outlined' style={{fontSize: '48px', opacity: 0.2, display: 'block', marginBottom: '1rem'}}>rate_review</span>
                      {intl.formatMessage({id: 'REVIEWS.TABLE.EMPTY'})}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{padding: '1.25rem 2rem', borderTop: '1px solid #eceef0', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <span style={{fontSize: '12px', color: '#434652', fontWeight: 500}}>
              {intl.formatMessage(
                {id: 'REVIEWS.PAGINATION.INFO'},
                {
                  from: pagination.currentPage * pagination.pageSize + 1,
                  to: Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalItems),
                  total: pagination.totalItems,
                }
              )}
            </span>
            <div style={{display: 'flex', gap: '0.25rem'}}>
              <button
                style={{
                  width: '2rem', height: '2rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(195,198,212,0.3)',
                  backgroundColor: 'transparent',
                  cursor: page <= 0 ? 'not-allowed' : 'pointer',
                  opacity: page <= 0 ? 0.4 : 1,
                  color: '#434652',
                }}
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <span className='material-symbols-outlined' style={{fontSize: '16px'}}>chevron_left</span>
              </button>
              {Array.from({length: Math.min(pagination.totalPages, 5)}, (_, i) => i).map((i) => (
                <button
                  key={i}
                  style={{
                    width: '2rem', height: '2rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '0.5rem',
                    border: i === page ? 'none' : '1px solid rgba(195,198,212,0.3)',
                    backgroundColor: i === page ? '#00347f' : 'transparent',
                    color: i === page ? 'white' : '#434652',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onClick={() => setPage(i)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                style={{
                  width: '2rem', height: '2rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(195,198,212,0.3)',
                  backgroundColor: 'transparent',
                  cursor: page >= pagination.totalPages - 1 ? 'not-allowed' : 'pointer',
                  opacity: page >= pagination.totalPages - 1 ? 0.4 : 1,
                  color: '#434652',
                }}
                disabled={page >= pagination.totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                <span className='material-symbols-outlined' style={{fontSize: '16px'}}>chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Insights Cards ───────────────────────────────────────────────── */}
      <div className='reviews-kpi-grid' style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem'}}>
        {/* Global Sentiment */}
        <div style={{padding: '1.5rem', backgroundColor: '#00347f', borderRadius: '1.5rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <div>
            <h4 style={{color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.25rem', margin: 0}}>
              Global Sentiment
            </h4>
            <p style={{fontSize: '1.875rem', fontWeight: 800, fontFamily: 'Manrope, sans-serif', margin: '0.25rem 0 0'}}>
              {(data?.payload as any)?.averageRating ? `${Number((data?.payload as any).averageRating).toFixed(1)} / 5.0` : '— / 5.0'}
            </p>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem'}}>
            <span className='material-symbols-outlined' style={{fontSize: '20px', color: '#ffb870'}}>trending_up</span>
            <span style={{fontSize: '12px', color: 'rgba(255,255,255,0.8)'}}>Based on {pagination?.totalItems ?? 0} reviews</span>
          </div>
        </div>

        {/* Pending Reviews */}
        <div style={{padding: '1.5rem', backgroundColor: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 24px -4px rgba(0,52,127,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <div>
            <h4 style={{color: 'rgba(67,70,82,0.6)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.25rem', margin: 0}}>
              Awaiting Moderation
            </h4>
            <p style={{fontSize: '1.875rem', fontWeight: 800, fontFamily: 'Manrope, sans-serif', color: '#006876', margin: '0.25rem 0 0'}}>
              {(data?.payload as any)?.pendingCount ?? 0} Pending
            </p>
          </div>
          <div style={{marginTop: '1rem'}}>
            <div style={{height: '6px', backgroundColor: '#eceef0', borderRadius: '9999px', overflow: 'hidden'}}>
              <div style={{height: '100%', backgroundColor: '#006876', borderRadius: '9999px', width: `${Math.min(100, (((data?.payload as any)?.pendingCount ?? 0) / Math.max(1, pagination?.totalItems ?? 1)) * 100)}%`}} />
            </div>
          </div>
        </div>

        {/* Total Reviews */}
        <div style={{padding: '1.5rem', backgroundColor: '#f2f4f6', borderRadius: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <div>
            <h4 style={{color: 'rgba(67,70,82,0.6)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.25rem', margin: 0}}>
              Total Reviews
            </h4>
            <p style={{fontSize: '1.875rem', fontWeight: 800, fontFamily: 'Manrope, sans-serif', color: '#191c1e', margin: '0.25rem 0 0'}}>
              {pagination?.totalItems ?? 0}
            </p>
          </div>
          <p style={{marginTop: '1rem', fontSize: '12px', fontWeight: 500, color: '#434652', margin: '1rem 0 0'}}>
            Across all tours and activities
          </p>
        </div>
      </div>

      {/* ── Modals (unchanged logic) ─────────────────────────────────────── */}
      <Modal show={!!detail} onHide={() => setDetail(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'REVIEWS.DETAIL.TITLE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              <p><strong>{intl.formatMessage({id: 'REVIEWS.TABLE.AUTHOR'})}:</strong> {detail.authorName}</p>
              <p><strong>{intl.formatMessage({id: 'REVIEWS.TABLE.RATING'})}:</strong> <StarRatingDisplay value={detail.rating} /></p>
              {detail.title && <p><strong>{intl.formatMessage({id: 'REVIEWS.DETAIL.TITLE_FIELD'})}:</strong> {detail.title}</p>}
              <p className='mb-1'><strong>{intl.formatMessage({id: 'REVIEWS.DETAIL.COMMENT'})}:</strong></p>
              <div className='bg-light rounded p-3 mb-3' style={{whiteSpace: 'pre-wrap'}}>{detail.comment}</div>
              {detail.source !== 'internal' && detail.externalUrl && (
                <p><a href={detail.externalUrl} target='_blank' rel='noreferrer'>{intl.formatMessage({id: 'REVIEWS.DETAIL.EXTERNAL_LINK'})}</a></p>
              )}
              {detail.source === 'internal' && detail.reservation && (
                <div className='border rounded p-3'>
                  <h6>{intl.formatMessage({id: 'REVIEWS.DETAIL.RESERVATION'})}</h6>
                  <pre className='small mb-0' style={{whiteSpace: 'pre-wrap'}}>{JSON.stringify(detail.reservation, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!rejectTarget} onHide={() => setRejectTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'REVIEWS.REJECT.TITLE'})}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{moderationNote: ''}}
          validationSchema={Yup.object({moderationNote: Yup.string().max(500)})}
          onSubmit={(values) => {
            if (!rejectTarget?.length) return
            modMut.mutate({ids: rejectTarget, status: 'rejected', note: values.moderationNote || undefined})
          }}
        >
          {({values, errors, touched, handleChange, handleSubmit}) => (
            <FormikForm onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group>
                  <Form.Label>{intl.formatMessage({id: 'REVIEWS.REJECT.NOTE'})}</Form.Label>
                  <Form.Control as='textarea' rows={3} name='moderationNote' value={values.moderationNote} onChange={handleChange} isInvalid={touched.moderationNote && !!errors.moderationNote} />
                  <Form.Control.Feedback type='invalid'>{errors.moderationNote}</Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='light' onClick={() => setRejectTarget(null)}>{intl.formatMessage({id: 'REVIEWS.MODAL.CANCEL'})}</Button>
                <Button variant='danger' type='submit' disabled={modMut.isLoading}>{intl.formatMessage({id: 'REVIEWS.ACTION.REJECT'})}</Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <Modal show={!!replyTarget} onHide={() => setReplyTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'REVIEWS.REPLY.TITLE'})}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{adminReply: ''}}
          validationSchema={Yup.object({adminReply: Yup.string().min(5).max(2000).required()})}
          onSubmit={(values) => {
            if (!replyTarget) return
            replyMut.mutate({id: replyTarget._id, text: values.adminReply})
          }}
        >
          {({values, errors, touched, handleChange, handleSubmit}) => (
            <FormikForm onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group>
                  <Form.Label>{intl.formatMessage({id: 'REVIEWS.REPLY.LABEL'})}</Form.Label>
                  <Form.Control as='textarea' rows={4} name='adminReply' value={values.adminReply} onChange={handleChange} isInvalid={touched.adminReply && !!errors.adminReply} />
                  <Form.Control.Feedback type='invalid'>{errors.adminReply}</Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='light' onClick={() => setReplyTarget(null)}>{intl.formatMessage({id: 'REVIEWS.MODAL.CANCEL'})}</Button>
                <Button variant='primary' type='submit' disabled={replyMut.isLoading}>{intl.formatMessage({id: 'REVIEWS.REPLY.SUBMIT'})}</Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'REVIEWS.DELETE.TITLE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{intl.formatMessage({id: 'REVIEWS.DELETE.CONFIRM'})}</Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setDeleteTarget(null)}>{intl.formatMessage({id: 'REVIEWS.MODAL.CANCEL'})}</Button>
          <Button variant='danger' disabled={delMut.isLoading} onClick={() => deleteTarget && delMut.mutate(deleteTarget._id)}>
            {intl.formatMessage({id: 'REVIEWS.ACTION.DELETE'})}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {ReviewsModerationTab}
